import React, { useEffect, useState, useRef } from "react";
import { Card, Row, Col, Form, Input, Button, Upload, Select, Divider } from "antd";
import { CameraOutlined, EditOutlined, SaveOutlined, CloseOutlined } from "@ant-design/icons";
import { useIsMobile } from "../../../hooks/useIsMobile";
import BaseService from "../../../service/base.service";
import ClinicDetails from "../../../models/clinic/clinicDetails";
import notify from "../../../components/common/notificationHelper";
import { useAppDispatch } from "../../../hooks/redux-hooks";
import { logout } from "../../../slices/authSlice";
import { useNavigate } from "react-router-dom";

interface ClinicFormValues {
  name: string;
  description: string;
  streetName: string;
  streetNumber: string;
  locationDescription: string;
  postalCode: string;
  city: string;
  country: string;
  phoneNumbers?: string[];
  email: string;
  website: string;
}

const ClinicInfoCard: React.FC = () => {
  const isMobile = useIsMobile();
  const [clinicDetails, setClinicDetails] = useState<ClinicDetails>();
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [form] = Form.useForm<ClinicFormValues>();
  const hasFetchedRef = useRef(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      handleGetMyClinicDetails();
    }
  }, []);

  const handleGetMyClinicDetails = async () => {
    try {
      const rp = await BaseService.get<ClinicDetails>("/clinics/details", null);
      if (rp.Status) {
        const data = rp.Data;
        setClinicDetails(data);
        if (data.logoUrl) setLogoUrl(data.logoUrl);
        form.setFieldsValue({
          name: data.name || "",
          description: data.description || "",
          streetName: data.streetName || "",
          streetNumber: data.streetNumber || "",
          locationDescription: data.locationDescription || "",
          postalCode: data.postalCode || "",
          city: data.city || "",
          country: data.country || "",
          phoneNumbers: data.phoneNumbers || [],
          email: data.email || "",
          website: data.website || "",
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleLogout = async () => {
    try {
      await dispatch(logout()).unwrap();
      navigate("/login");
    } catch (e) {
      console.error(e);
    }
  };

  const handleUploadLogo = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const rp = await BaseService.upload("/clinics/logo", formData, null);
      if (rp.Status) {
        notify({
          title: "Success",
          description: `Clinic logo updated.`,
          variant: "success",
        });
        handleGetMyClinicDetails();
        setEditing(false);
      } else {
        notify({
          title: "Error",
          description: `Failed to update clinic logo.`,
          variant: "error",
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSave = async (values: ClinicFormValues) => {
    try {
      const rp = await BaseService.update("/clinics/details", null, values);
      if (rp.Status) {
        notify({
          title: "Success",
          description: `Clinic details updated.`,
          variant: "success",
        });
        handleGetMyClinicDetails();
        setEditing(false);
      } else {
        notify({
          title: "Error",
          description: `Failed to update clinic details.`,
          variant: "error",
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleCancel = () => {
    setEditing(false);
  };

  return (
    <Card
      style={{
        width: "100%",
        borderRadius: isMobile ? 12 : 16,
        boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
        padding: isMobile ? 12 : 24,
        backgroundColor: "#fff",
      }}
    >
      <Row
        justify="space-between"
        align={isMobile ? "top" : "middle"}
        gutter={[12, 16]}
        style={{ marginBottom: 24 }}
      >
        <Col xs={24} md={16}>
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "center" : "center",
              gap: 16,
              textAlign: isMobile ? "center" : "left",
            }}
          >
            {/* Clinic Logo Card with Upload Button */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 12,
              }}
            >
              {/* Logo Preview Box */}
              <div
                style={{
                  width: isMobile ? 110 : 140,
                  height: isMobile ? 110 : 140,
                  border: "2px dashed #d9d9d9",
                  borderRadius: 12,
                  backgroundColor: "#fafafa",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  overflow: "hidden",
                  position: "relative",
                  transition: "all 0.3s ease",
                }}
                className="clinic-logo-box"
              >
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt="Clinic Logo"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      padding: 8,
                    }}
                  />
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      color: "#999",
                      fontSize: 14,
                    }}
                  >
                    <CameraOutlined style={{ fontSize: 28, color: "#fa8c16" }} />
                    <div style={{ marginTop: 4 }}>No logo uploaded</div>
                  </div>
                )}
              </div>

              {/* Upload Button Below */}
              <Upload showUploadList={false} beforeUpload={handleUploadLogo}>
                <Button
                  icon={<CameraOutlined />}
                  type="primary"
                  style={{
                    backgroundColor: "#fa8c16",
                    borderColor: "#fa8c16",
                    borderRadius: 8,
                  }}
                >
                  {logoUrl ? "Change Logo" : "Upload Logo"}
                </Button>
              </Upload>
            </div>



            <div>
              <div style={{ fontWeight: 600, fontSize: 18 }}>
                {clinicDetails?.name || "---"}
              </div>
              <div style={{ color: "#888" }}>{clinicDetails?.email || "---"}</div>
            </div>
          </div>
        </Col>

        <Col xs={24} md={8} style={{ textAlign: isMobile ? "center" : "right" }}>
          {!editing ? (
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => setEditing(true)}
              block={isMobile}
            >
              Edit
            </Button>
          ) : (
            <div
              style={{
                display: "flex",
                gap: 8,
                justifyContent: isMobile ? "stretch" : "flex-end",
              }}
            >
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={() => form.submit()}
                block={isMobile}
              >
                Save
              </Button>
              <Button
                icon={<CloseOutlined />}
                onClick={handleCancel}
                block={isMobile}
              >
                Cancel
              </Button>
            </div>
          )}
        </Col>
      </Row>

      {/* Form */}
      <Form
        form={form}
        layout="vertical"
        disabled={!editing}
        onFinish={handleSave}
      >
        {/* Basic Info */}
        <Divider orientation="left">Basic Info</Divider>
        <Row gutter={[16, 8]}>
          <Col xs={24} md={12}>
            <Form.Item name="name" label="Clinic Name">
              <Input placeholder="Enter clinic name" className="form-input" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="description" label="Description">
              <Input placeholder="Enter description" className="form-input" />
            </Form.Item>
          </Col>
        </Row>

        {/* Address Info */}
        <Divider orientation="left">Address</Divider>
        <Row gutter={[16, 8]}>
          <Col xs={24} md={12}>
            <Form.Item name="streetName" label="Street Name">
              <Input placeholder="Street name" className="form-input" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="streetNumber" label="Street Number">
              <Input placeholder="Street number" className="form-input" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="postalCode" label="Postal Code">
              <Input placeholder="Postal code" className="form-input" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="city" label="City">
              <Input placeholder="City" className="form-input" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="country" label="Country">
              <Input placeholder="Country" className="form-input" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="locationDescription" label="Location Description">
              <Input placeholder="Location description" className="form-input" />
            </Form.Item>
          </Col>
        </Row>

        {/* Contact Info */}
        <Divider orientation="left">Contact Info</Divider>
        <Row gutter={[16, 8]}>
          <Col xs={24} md={12}>
            <Form.Item name="phoneNumbers" label="Phone Numbers">
              <Select
                mode="tags"
                placeholder="Enter phone numbers"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="email" label="Email">
              <Input placeholder="Email address" className="form-input" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="website" label="Website">
              <Input placeholder="Website URL" className="form-input" />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      {/* Placeholder styling */}
      <style>{`
        .form-input::placeholder {
          color: #aaa;
        }
        .form-input {
          color: #000;
        }
      `}</style>
    </Card>
  );
};

export default ClinicInfoCard;
